import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { createPortal } from 'react-dom';

const getTargetElement = (target) => {
  if (!target) return null;
  if (typeof target === 'string') return document.querySelector(target);
  return target.current || null;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const OnboardingTour = ({
  open,
  steps = [],
  stepIndex = 0,
  onBack,
  onNext,
  onClose,
  onFinish,
  nextDisabled = false,
}) => {
  const [rect, setRect] = useState(null);

  const step = steps[stepIndex];
  const totalSteps = steps.length;
  const isLastStep = stepIndex >= totalSteps - 1;

  useEffect(() => {
    if (!open || !step) return undefined;

    let didScroll = false;
    let attempts = 0;

    const updateRect = () => {
      const modalTarget = step.targetModal ? getTargetElement(step.targetModal) : null;
      const el = modalTarget || getTargetElement(step.target);
      if (!el) {
        setRect(null);
        return false;
      }
      const bounds = el.getBoundingClientRect();
      setRect({
        top: bounds.top,
        left: bounds.left,
        width: bounds.width,
        height: bounds.height,
      });
      if (step.scrollIntoView && !didScroll) {
        didScroll = true;
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      return true;
    };

    updateRect();
    const retryTimer = setInterval(() => {
      if (step.targetModal) {
        updateRect();
        return;
      }
      attempts += 1;
      const found = updateRect();
      if (found || attempts > 8) {
        clearInterval(retryTimer);
      }
    }, 250);
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      clearInterval(retryTimer);
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [open, stepIndex, step?.target, step?.targetModal, step?.scrollIntoView, step]);

  const overlayRect = useMemo(() => {
    if (!rect) return null;
    const padding = step?.padding ?? 10;
    return {
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    };
  }, [rect, step]);

  if (!open || !step) return null;

  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 768;
  const panelWidth = 360;
  const panelHeight = 220;
  const margin = 16;

  let panelPosition = {
    left: '50%',
    bottom: 24,
    transform: 'translateX(-50%)',
  };

  if (step?.panelPosition === 'bottom-right') {
    panelPosition = {
      right: 24,
      bottom: 24,
    };
  } else if (step?.panelPosition === 'bottom-left') {
    panelPosition = {
      left: 24,
      bottom: 24,
    };
  } else if (overlayRect) {
    const spaceBelow = viewportHeight - (overlayRect.top + overlayRect.height);
    const preferBelow = spaceBelow > panelHeight + 24;
    const top = preferBelow
      ? overlayRect.top + overlayRect.height + 12
      : overlayRect.top - panelHeight - 12;
    const left = clamp(
      overlayRect.left,
      margin,
      viewportWidth - panelWidth - margin
    );
    panelPosition = {
      top: Math.max(margin, top),
      left,
    };
  }

  const descriptions = Array.isArray(step.description)
    ? step.description
    : [step.description].filter(Boolean);

  return createPortal(
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 2000, pointerEvents: 'none' }}>
      {overlayRect ? (
        <Box
          sx={{
            position: 'fixed',
            top: overlayRect.top,
            left: overlayRect.left,
            width: overlayRect.width,
            height: overlayRect.height,
            borderRadius: 2,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
            border: '2px solid #ffffff',
            transition: 'all 0.2s ease',
            pointerEvents: 'none',
          }}
        />
      ) : (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.6)',
            pointerEvents: 'none',
          }}
        />
      )}

      <Box
        sx={{
          position: 'fixed',
          width: panelWidth,
          maxWidth: 'calc(100vw - 32px)',
          zIndex: 2001,
          ...panelPosition,
          pointerEvents: 'auto',
        }}
      >
        <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 6 }}>
          <Typography variant="overline" sx={{ fontWeight: 700, color: '#d30038' }}>
            Passo {Math.min(stepIndex + 1, totalSteps)} de {totalSteps}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
            {step.title}
          </Typography>
          {descriptions.map((text, index) => (
            <Typography key={`${step.id}-desc-${index}`} variant="body2" sx={{ mt: 1, color: '#4b5563' }}>
              {text}
            </Typography>
          ))}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', mt: 2 }}>
            <Box>
              {stepIndex > 0 && (
                <Button variant="text" onClick={onBack}>
                  Voltar
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="text" color="inherit" onClick={onClose}>
                Continuar depois
              </Button>
              <Button
                variant="contained"
                onClick={isLastStep ? onFinish : onNext}
                disabled={nextDisabled}
              >
                {isLastStep ? 'Concluir' : 'Próximo'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>,
    document.body
  );
};

export default OnboardingTour;
